import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { mtproAPI, customersAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import {
  Search, Plus, ChevronDown, ChevronRight, Building2, Users, Server,
  Globe, ExternalLink, Pencil, CheckCircle2, Circle, RefreshCw
} from 'lucide-react';

const NAVY = '#1e3a5f';
const ORANGE = '#ea580c';

function StatusBadge({ status }) {
  const isActive = status === 'active';
  return (
    <span className="text-xs font-medium px-2 py-0.5 rounded-full"
      style={isActive
        ? { background: '#dcfce7', color: '#15803d' }
        : { background: '#f1f5f9', color: '#64748b' }}>
      {isActive ? '● Active' : '○ Inactive'}
    </span>
  );
}

function TenantRow({ tenant }) {
  return (
    <div className="flex items-center gap-3 py-1.5 px-3 rounded-lg" style={{ background: '#f8fafc' }}>
      <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0"
        style={{ background: 'hsl(265 89% 93%)', color: 'hsl(265 89% 45%)', fontSize: '10px', fontWeight: 700 }}>
        T
      </div>
      <p className="text-sm font-medium flex-1" style={{ color: NAVY }}>{tenant.company_name}</p>
      <span className="text-xs font-bold tabular-nums" style={{ color: ORANGE }}>{tenant.num_users || 0} users</span>
    </div>
  );
}

function SystemRow({ system, onCreateAddition, onUpdated, navigate }) {
  const [expanded, setExpanded]     = useState(false);
  const [detail, setDetail]         = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [editing, setEditing]       = useState(false);
  const [editName, setEditName]     = useState(system.name || '');
  const [savingEdit, setSavingEdit] = useState(false);

  const loadDetail = async () => {
    if (detail) return;
    setLoadingDetail(true);
    try {
      const r = await mtproAPI.getSystem(system.id);
      setDetail(r.data);
    } catch { toast.error('Failed to load system details'); }
    finally { setLoadingDetail(false); }
  };

  const toggleExpand = () => {
    if (!expanded) loadDetail();
    setExpanded(e => !e);
  };

  const saveEdit = async () => {
    if (!editName.trim()) return;
    setSavingEdit(true);
    try {
      await mtproAPI.updateSystem(system.id, { name: editName.trim() });
      toast.success('System name updated');
      setEditing(false);
      onUpdated();
    } catch { toast.error('Failed to update'); }
    finally { setSavingEdit(false); }
  };

  const toggleStatus = async () => {
    const newStatus = system.status === 'active' ? 'inactive' : 'active';
    try {
      await mtproAPI.updateSystem(system.id, { status: newStatus });
      toast.success(`System ${newStatus}`);
      onUpdated();
    } catch { toast.error('Failed to update status'); }
  };

  const tenants = detail?.tenants || [];

  return (
    <div className="rounded-xl border bg-white overflow-hidden transition-shadow hover:shadow-sm cursor-pointer"
      onClick={() => navigate(`/mtpro-systems/${system.id}`)} data-testid={`system-row-${system.id}`}>
      {/* Main row */}
      <div className="p-4">
        <div className="flex items-start gap-3 flex-wrap">
          {/* Expand toggle */}
          <button onClick={e => { e.stopPropagation(); toggleExpand(); }} className="mt-0.5 flex-shrink-0 text-muted-foreground hover:text-foreground transition-colors">
            {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          </button>

          {/* System info */}
          <div className="flex-1 min-w-0">
            {editing ? (
              <div className="flex items-center gap-2 mb-1">
                <Input value={editName} onChange={e => setEditName(e.target.value)}
                  className="h-7 text-sm font-semibold w-64" autoFocus
                  onKeyDown={e => { if (e.key === 'Enter') saveEdit(); if (e.key === 'Escape') setEditing(false); }} />
                <Button size="sm" className="h-7 px-2 text-xs" onClick={saveEdit} disabled={savingEdit}
                  style={{ background: NAVY, color: 'white' }}>Save</Button>
                <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => setEditing(false)}>Cancel</Button>
              </div>
            ) : (
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <h3 className="font-bold text-sm" style={{ color: NAVY }}>{system.name}</h3>
                <StatusBadge status={system.status} />
                {!editing && (
                  <button onClick={() => { setEditName(system.name); setEditing(true); }}
                    className="opacity-0 group-hover:opacity-100 transition-opacity"
                    style={{ color: 'hsl(var(--muted-fg))' }}>
                    <Pencil size={11} />
                  </button>
                )}
              </div>
            )}
            <div className="flex items-center gap-4 flex-wrap text-xs" style={{ color: 'hsl(var(--muted-fg))' }}>
              <span className="flex items-center gap-1"><Building2 size={11} /> {system.partner_name}</span>
              {system.region_name && <span className="flex items-center gap-1"><Globe size={11} /> {system.region_name}</span>}
              <span className="flex items-center gap-1"><Users size={11} /> {system.num_users} users</span>
              <span className="flex items-center gap-1"><Server size={11} /> Deployment</span>
            </div>
            {system.origin_quote_number && (
              <p className="text-xs mt-1" style={{ color: 'hsl(var(--muted-fg))' }}>
                Origin: <span className="font-mono">{system.origin_quote_number}</span>
              </p>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 flex-shrink-0" onClick={e => e.stopPropagation()}>
            <Button size="sm" variant="ghost" className="h-7 w-7 p-0" title="Edit name"
              onClick={() => { setEditName(system.name); setEditing(true); }}
              style={{ color: 'hsl(var(--muted-fg))' }}>
              <Pencil size={12} />
            </Button>
            <Button size="sm" variant="ghost" className="h-7 px-2 text-xs gap-1"
              title={system.status === 'active' ? 'Deactivate' : 'Activate'}
              onClick={toggleStatus}
              style={{ color: system.status === 'active' ? '#d97706' : '#16a34a' }}>
              {system.status === 'active' ? <Circle size={11} /> : <CheckCircle2 size={11} />}
              {system.status === 'active' ? 'Deactivate' : 'Activate'}
            </Button>
            <Button size="sm" onClick={() => onCreateAddition(system)} className="gap-1.5 h-7 px-3 text-xs"
              style={{ background: 'hsl(265 89% 45%)', color: 'white' }}
              data-testid={`mtpro-add-to-system-${system.id}`}>
              <Plus size={11} /> Add to System
            </Button>
          </div>
        </div>
      </div>

      {/* Expanded tenant list */}
      {expanded && (
        <div className="border-t px-4 py-3" style={{ background: '#fafafa' }}>
          <p className="text-xs font-bold uppercase tracking-wide mb-2" style={{ color: 'hsl(var(--muted-fg))' }}>
            Tenants on this system
          </p>
          {loadingDetail ? (
            <div className="space-y-1.5">
              {[1,2].map(i => <Skeleton key={i} className="h-8 w-full rounded-lg" />)}
            </div>
          ) : tenants.length === 0 ? (
            <p className="text-xs italic" style={{ color: 'hsl(var(--muted-fg))' }}>
              No tenants linked yet. Link MTPro tenants via the System Detail page (click to open).
            </p>
          ) : (
            <div className="space-y-1.5">
              {tenants.map(t => <TenantRow key={t.id} tenant={t} />)}
              <p className="text-xs mt-2 font-semibold" style={{ color: NAVY }}>
                Total: {tenants.reduce((s, t) => s + (t.num_users || 0), 0)} users across {tenants.length} tenant{tenants.length !== 1 ? 's' : ''}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function MtproSystemsPage() {
  const { isAdmin, isManager } = useAuth();
  const navigate = useNavigate();  const [systems, setSystems]     = useState([]);
  const [partners, setPartners]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [search, setSearch]       = useState('');
  const [partnerFilter, setPartnerFilter] = useState('');
  const [statusFilter, setStatusFilter]   = useState('active');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter && statusFilter !== 'all') params.status = statusFilter;
      if (partnerFilter) params.partner_id = partnerFilter;
      const r = await mtproAPI.listSystems(params);
      setSystems(r.data || []);
    } catch { toast.error('Failed to load MTPro systems'); }
    finally { setLoading(false); }
  }, [statusFilter, partnerFilter]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    customersAPI.list({ page_size: 200, status: 'active' })
      .then(r => setPartners(r.data?.items || []))
      .catch(() => {});
  }, []);

  const filtered = systems.filter(s =>
    !search || s.name?.toLowerCase().includes(search.toLowerCase()) ||
    s.partner_name?.toLowerCase().includes(search.toLowerCase()) ||
    s.region_name?.toLowerCase().includes(search.toLowerCase())
  );

  // Stats
  const totalUsers   = systems.reduce((s, sys) => s + (sys.num_users || 0), 0);
  const activeSystems = systems.filter(s => s.status === 'active').length;

  const handleCreateAddition = (system) => {
    navigate(`/quotes/new?product=MTPRO&addition=1&system_id=${system.id}&partner_id=${system.partner_id}&partner_name=${encodeURIComponent(system.partner_name || '')}`);
  };

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      {/* Page header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'var(--font-display)', color: NAVY }}>
            MTPro Systems
          </h1>
          <p className="text-sm mt-0.5" style={{ color: 'hsl(var(--muted-fg))' }}>
            Live multi-tenant deployments across all partners
          </p>
        </div>
        <Button variant="ghost" size="sm" onClick={load} className="gap-1.5" style={{ color: 'hsl(var(--muted-fg))' }}>
          <RefreshCw size={13} /> Refresh
        </Button>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: 'Live Systems', value: activeSystems, icon: Server, color: '#0ea5e9' },
          { label: 'Total Users', value: totalUsers, icon: Users, color: ORANGE },
          { label: 'All Systems', value: systems.length, icon: Globe, color: 'hsl(265 89% 45%)' },
        ].map(({ label, value, icon: Icon, color }) => (
          <div key={label} className="rounded-xl border bg-white p-4 flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
              style={{ background: color + '1a' }}>
              <Icon size={16} style={{ color }} />
            </div>
            <div>
              <p className="text-2xl font-bold tabular-nums" style={{ color: NAVY }}>{value}</p>
              <p className="text-xs" style={{ color: 'hsl(var(--muted-fg))' }}>{label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4" style={{ color: 'hsl(var(--muted-fg))' }} />
          <Input placeholder="Search by name, partner or region…" value={search}
            onChange={e => setSearch(e.target.value)} className="pl-9" />
        </div>
        <Select value={partnerFilter || 'all'} onValueChange={v => setPartnerFilter(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-48"><SelectValue placeholder="All partners" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All partners</SelectItem>
            {partners.map(p => <SelectItem key={p.id} value={p.id}>{p.company_name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
            <SelectItem value="all">All</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Systems list */}
      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map(i => <Skeleton key={i} className="h-24 w-full rounded-xl" />)}
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded-xl border border-dashed bg-white p-12 text-center">
          <div className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-4"
            style={{ background: 'hsl(265 89% 95%)' }}>
            <Server size={24} style={{ color: 'hsl(265 89% 45%)' }} />
          </div>
          <h3 className="font-bold text-lg mb-1" style={{ color: NAVY }}>
            {systems.length === 0 ? 'No MTPro systems yet' : 'No systems match your filter'}
          </h3>
          <p className="text-sm" style={{ color: 'hsl(var(--muted-fg))' }}>
            {systems.length === 0
              ? 'MTPro service units appear here automatically when an MTPro quote is marked as Accepted.'
              : 'Try adjusting your search or filter.'}
          </p>
          {systems.length === 0 && (
            <div className="mt-4 rounded-lg p-3 text-xs text-left inline-block" style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', color: '#14532d' }}>
              <strong>How it works:</strong> Build an MTPro quote → get it accepted → a service unit record is automatically created here.
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-3 group">
          {filtered.map(system => (
            <SystemRow
              key={system.id}
              system={system}
              navigate={navigate}
              onCreateAddition={handleCreateAddition}
              onUpdated={load}
            />
          ))}
        </div>
      )}
    </div>
  );
}
